import EmptyState from 'components/EmptyState';
import { useFormatDate } from 'components/misc/FormattedDate';
import {
    BOTH_DESIGNS_DIALOG_640,
    BOTH_DESIGNS_DIALOG_800,
    BOTH_DESIGNS_DIALOG_900,
} from 'components/modals/dialogWidths';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { Skeleton } from 'components/ui/skeleton';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGetIdentificationPanelQuery } from 'store/api/client-identifications';
import { ClassifierData } from 'types/classifiers';
import {
    ClientIdentificationDto,
    ClientIdentificationPanelDto,
    MissingItemBlock,
    PanelPersonDto,
    PanelTypedRowDto,
    RelatedPartiesDto,
} from 'types/clientIdentification';
import { PartyType } from 'types/contacts';
import { Contract } from 'types/contracts';
import { t } from 'utils/getTranslations';
import { useDrawsOwnLoadFailure } from 'utils/useDrawsOwnLoadFailure';
import { Block, blockTitleId, BlockTone } from './blocks/Block';
import { BLOCK_IDS, BlockId } from './blocks/blockIds';
import { ClientAndTierBlock, ClientAndTierEditModal, ClientParty } from './blocks/ClientAndTierBlock';
import { ClientDetailsBlock } from './blocks/ClientDetailsBlock';
import { ClientDetailsEditModal } from './blocks/ClientDetailsEditModal';
import { RelatedPartiesBlock } from './blocks/RelatedPartiesBlock';
import { RelatedPartiesEditModal } from './blocks/RelatedPartiesEditModal';
import { EvidenceBlock, EvidenceEditModal } from './blocks/EvidenceBlock';
import { IdentificationBlock } from './blocks/IdentificationBlock';
import { PersonCardEditModal, PersonCardSubject } from './blocks/PersonCardEditModal';
import { ReviewBlock } from './blocks/ReviewBlock';
import { ContentsEntry, ContentsPanel } from './ContentsPanel';
import { personNames } from './identityValues';
import { useClientIdentificationPopup } from './clientIdentificationPopup';
import { receivedLine } from './receiptDates';
import { useTierName } from './tierOptions';

const BLOCK_POPUP = BOTH_DESIGNS_DIALOG_800;
const PERSON_POPUP = BOTH_DESIGNS_DIALOG_640;
const RELATED_PARTIES_POPUP = BOTH_DESIGNS_DIALOG_900;
const NO_RELATED_PARTIES: RelatedPartiesDto = { groups: [], notAskedAtTier: [] };
const BLOCKS_LAYOUT_CLASS = 'grid grid-cols-1 items-start gap-4 lg:grid-cols-[240px_minmax(0,1fr)]';
const BLOCK_COLUMN_CLASS = 'new:gap-(--card-gap) flex max-w-[880px] min-w-0 flex-col gap-3';

interface BlockEntry extends ContentsEntry {
    missingCount: number;
    onEdit?: () => void;
    body: ReactNode;
}

interface Props {
    contract: Contract;
    assessment: ClientIdentificationDto;
    classifiers: Record<string, ClassifierData>;
}

/** What is missing is the panel's say alone: nothing here recomputes it. */
export function ClientIdentificationForm({ contract, assessment, classifiers }: Props) {
    const { data: panel, isLoading, isError, refetch } = useGetIdentificationPanelQuery(contract.id);
    useDrawsOwnLoadFailure('getIdentificationPanel', contract.id, !!panel);

    if (isLoading) {
        return <BlocksSkeleton />;
    }
    if (!panel) {
        return isError ? (
            <EmptyState
                variant="error"
                errorTitleId="client-identification.panel-load-error"
                onRetry={() => void refetch()}
            />
        ) : null;
    }
    return (
        <ClientIdentificationBlocks
            contract={contract}
            assessment={assessment}
            panel={panel}
            classifiers={classifiers}
        />
    );
}

function BlocksSkeleton() {
    return (
        <div aria-busy="true" className={BLOCKS_LAYOUT_CLASS}>
            <Skeleton className="h-72 rounded-[10px]" />
            <div className={BLOCK_COLUMN_CLASS}>
                {Object.values(BLOCK_IDS).map((id) => (
                    <Skeleton key={id} className="h-16 rounded-[10px]" />
                ))}
            </div>
        </div>
    );
}

function ClientIdentificationBlocks({
    contract,
    assessment,
    panel,
    classifiers,
}: Props & { panel: ClientIdentificationPanelDto }) {
    const { openPopup, closePopup: closeModal } = useClientIdentificationPopup();
    const reducedMotion = useReducedMotion();
    const formatDate = useFormatDate();
    const intl = useIntl();
    const tierName = useTierName(assessment.tierCode);
    const [closedBlocks, setClosedBlocks] = useState<Set<BlockId>>(() => new Set());

    const canEdit = assessment.mayEdit && !assessment.isLocked;
    const isDraft = assessment.statusShown === 'DRAFT';
    const client = clientParty(contract, panel);
    const missingIn = (block: MissingItemBlock) => panel.missingItems.filter((item) => item.block === block).length;

    const openBlockPopup = (content: JSX.Element) => openPopup(content, BLOCK_POPUP);
    const editPerson = (subject: PersonCardSubject) =>
        openPopup(
            <PersonCardEditModal
                contractId={contract.id}
                subject={subject}
                relatedParties={relatedParties}
                classifiers={classifiers}
                onClose={closeModal}
            />,
            PERSON_POPUP
        );

    const detailsMissing = missingIn('CLIENT_DETAILS');
    const identificationMissing = missingIn('IDENTIFICATION');
    const cardCount = panel.people.length + panel.typedRows.length;
    const relatedParties = contract.relatedParties ?? NO_RELATED_PARTIES;
    const relatedMissing = missingIn('RELATED_PARTIES') + panel.missingMandatoryRoles.length;
    const partyCount = relatedParties.groups.reduce((count, group) => count + group.rows.length, 0);

    const blocks: BlockEntry[] = [
        {
            id: BLOCK_IDS.clientAndTier,
            number: 1,
            title: t('client-identification.block.client-and-tier'),
            summary: [
                tierName,
                assessment.identifiedByName
                    ? t('client-identification.identified-by-line', { name: assessment.identifiedByName })
                    : null,
                assessment.responsibleName
                    ? t('client-identification.responsible-line', { name: assessment.responsibleName })
                    : null,
            ]
                .filter(Boolean)
                .join(' · '),
            tone: 'done',
            missingCount: 0,
            onEdit: canEdit
                ? () =>
                      openBlockPopup(
                          <ClientAndTierEditModal
                              contractId={contract.id}
                              client={client}
                              assessment={assessment}
                              onClose={closeModal}
                          />
                      )
                : undefined,
            body: <ClientAndTierBlock client={client} assessment={assessment} />,
        },
        {
            id: BLOCK_IDS.clientDetails,
            number: 2,
            title: t('client-identification.block.client-details'),
            summary: completionSummary(detailsMissing),
            tone: toneOf(detailsMissing),
            missingCount: detailsMissing,
            onEdit: canEdit
                ? () =>
                      openBlockPopup(
                          <ClientDetailsEditModal
                              contractId={contract.id}
                              client={client}
                              assessment={assessment}
                              panel={panel}
                              classifiers={classifiers}
                              onClose={closeModal}
                          />
                      )
                : undefined,
            body: <ClientDetailsBlock client={client} panel={panel} classifiers={classifiers} />,
        },
        {
            id: BLOCK_IDS.relatedParties,
            number: 3,
            title: t('client-identification.block.related-parties'),
            summary: [
                t('client-identification.n-parties', { count: String(partyCount) }),
                panel.missingMandatoryRoles.length
                    ? t('client-identification.missing-roles', {
                          roles: intl.formatList(
                              panel.missingMandatoryRoles.map((role) => role.namePlural),
                              { type: 'unit' }
                          ),
                      })
                    : null,
            ]
                .filter(Boolean)
                .join(' · '),
            tone: toneOf(relatedMissing),
            missingCount: relatedMissing,
            onEdit: canEdit
                ? () =>
                      openPopup(
                          <RelatedPartiesEditModal
                              contractId={contract.id}
                              assessment={assessment}
                              relatedParties={relatedParties}
                              panel={panel}
                              classifiers={classifiers}
                              onClose={closeModal}
                          />,
                          RELATED_PARTIES_POPUP
                      )
                : undefined,
            body: (
                <RelatedPartiesBlock
                    contractId={contract.id}
                    relatedParties={relatedParties}
                    panel={panel}
                    classifiers={classifiers}
                    canEdit={canEdit}
                />
            ),
        },
        {
            id: BLOCK_IDS.identification,
            number: 4,
            title: t('client-identification.block.identification'),
            summary: [
                t('client-identification.n-cards', { count: String(cardCount) }),
                identificationMissing ? completionSummary(identificationMissing) : null,
            ]
                .filter(Boolean)
                .join(' · '),
            tone: toneOf(identificationMissing),
            missingCount: identificationMissing,
            body: (
                <IdentificationBlock
                    panel={panel}
                    classifiers={classifiers}
                    onEditPerson={
                        canEdit ? (person: PanelPersonDto) => editPerson({ kind: 'person', person }) : undefined
                    }
                    onEditTypedRow={canEdit ? (row: PanelTypedRowDto) => editPerson({ kind: 'typed', row }) : undefined}
                />
            ),
        },
        {
            id: BLOCK_IDS.evidence,
            number: 5,
            title: t('client-identification.block.evidence'),
            summary: t('client-identification.evidence-summary', {
                link: contract.dmsLink ? 'set' : 'none',
                notes: contract.additionalNotes ? 'set' : 'none',
            }),
            tone: contract.dmsLink ? 'done' : 'plain',
            missingCount: 0,
            onEdit: canEdit
                ? () =>
                      openBlockPopup(
                          <EvidenceEditModal contract={contract} assessment={assessment} onClose={closeModal} />
                      )
                : undefined,
            body: <EvidenceBlock contract={contract} assessment={assessment} />,
        },
        {
            id: BLOCK_IDS.review,
            number: 6,
            title: t(isDraft ? 'client-identification.block.review' : 'client-identification.block.received'),
            summary: isDraft
                ? completionSummary(panel.missingCount)
                : receivedLine(formatDate(assessment.receivedOn) ?? '', assessment.receivedByName),
            tone: isDraft ? toneOf(panel.missingCount) : 'done',
            missingCount: isDraft ? panel.missingCount : 0,
            body: <ReviewBlock assessment={assessment} panel={panel} onJump={jumpTo} />,
        },
    ];

    function setOpen(id: BlockId, open: boolean) {
        setClosedBlocks((closed) => {
            const next = new Set(closed);
            if (open) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }

    function jumpTo(id: BlockId) {
        setOpen(id, true);
        document.getElementById(id)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        document.getElementById(blockTitleId(id))?.focus({ preventScroll: true });
    }

    return (
        <div className={BLOCKS_LAYOUT_CLASS}>
            <ContentsPanel
                // A sticky offset counts from inside the scroll area's padding, which already holds the gap above it.
                className="lg:sticky lg:top-0"
                entries={blocks}
                onJump={jumpTo}
                onExpandAll={() => setClosedBlocks(new Set())}
                onCollapseAll={() => setClosedBlocks(new Set(blocks.map((block) => block.id)))}
            />
            <div className={BLOCK_COLUMN_CLASS}>
                {blocks.map((block) => (
                    <Block
                        key={block.id}
                        id={block.id}
                        number={block.number}
                        title={block.title}
                        summary={block.summary}
                        tone={block.tone}
                        missingCount={block.missingCount}
                        onEdit={block.onEdit}
                        open={!closedBlocks.has(block.id)}
                        onOpenChange={(open) => setOpen(block.id, open)}
                    >
                        {block.body}
                    </Block>
                ))}
            </div>
        </div>
    );
}

function toneOf(missingCount: number): BlockTone {
    return missingCount ? 'missing' : 'done';
}

function completionSummary(missingCount: number): string {
    return missingCount
        ? t('client-identification.n-missing', { count: String(missingCount) })
        : t('client-identification.complete');
}

function clientParty(contract: Contract, panel: ClientIdentificationPanelDto): ClientParty {
    const party = contract.parties?.find((entry) => entry.party.id === panel.clientPartyId)?.party;
    const recordedName =
        panel.clientPartyType === PartyType.COMPANY
            ? (panel.clientFields.officialName ?? panel.clientFields.name)
            : personNames(panel.clientFields);
    return {
        id: panel.clientPartyId,
        name: party?.name ?? recordedName ?? '',
        partyType: panel.clientPartyType,
    };
}
